import { SPINE_BINDING_KEYS } from "./binding-keys";
import {
  answerPresent,
  enabledSet,
  flattenItems,
  isDisplayReadBound,
  repeatingSections,
  rowEnabledSet,
  storedAnswerPresent,
} from "./enable-when";
import {
  type AnswerMap,
  type FormDefinition,
  type FormItem,
  type ReadValues,
  type RowMap,
} from "./types";

/**
 * Client-side answer validation — the pure twin of the Go response validator
 * (apps/api/internal/compliance/formsubmission/validate.go), built entirely on
 * the shared evaluator primitives. Every client seal (`finalizeAnswers` — both
 * composer submit arms and mobile's finalize) runs each enabled item through
 * the per-item core; the composed validateAnswers is the whole-document walk.
 *
 * "rows" is keyed on the repeating SECTION link_id (malformed shape or the
 * per-collection cap); every other rule is keyed on an ITEM link_id.
 * "negative" is a typed number answer below zero (the server's
 * validation_negative_number).
 */
export interface AnswerValidationFinding {
  link_id: string;
  rule: "required" | "min" | "max" | "negative" | "max_length" | "option" | "attachment" | "rows";
}

/**
 * The server caps a repeating collection at 200 rows per section
 * (rows_too_many); the client gates mirror it so a tester can never submit a
 * collection the server is guaranteed to reject. Source of truth:
 * validate.go maxCollectionRows.
 */
export const MAX_COLLECTION_ROWS = 200;

const SPINE_BINDINGS: ReadonlySet<string> = new Set(SPINE_BINDING_KEYS);

/**
 * Twin of the server's isRequired: an item is required when its validation
 * flag is set OR its regulatory binding projects the spine
 * (Derived.Outcome / Derived.PerformedAt must be total for the receive
 * path — formtemplate.SpineBindingKeys + validate.go isRequired).
 */
export function isRequiredAnswer(item: FormItem): boolean {
  if (item.validation?.required === true) return true;
  return isSpineBound(item);
}

function isSpineBound(item: FormItem): boolean {
  return item.binding !== undefined && SPINE_BINDINGS.has(item.binding);
}

/** The client-minted attachment ids in a document answer. */
export function attachmentAnswerIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === "string" && v !== "");
}

/** The single signature id in a signature answer, or null. */
export function signatureAnswerId(raw: unknown): string | null {
  return typeof raw === "string" && raw !== "" ? raw : null;
}

/**
 * Whether an attachment-bearing item may carry more than one reference: a
 * document item that declares `repeats` (the publish gate admits the flag on
 * no other type). Any other document answer holds at most one — the server's
 * own multiplicity rule (validate.go attachmentFindings).
 */
export function multiAttachmentAllowed(item: FormItem): boolean {
  return item.type === "document" && item.repeats === true;
}

/** A repeating collection's raw answer is a row array (shape gate). */
export function isRowArray(raw: unknown): raw is RowMap[] {
  return (
    Array.isArray(raw) && raw.every((v) => typeof v === "object" && v !== null && !Array.isArray(v))
  );
}

/**
 * Validates ONE item's answer within a scope (the top-level document or one
 * row), mirroring the Go per-item pass: required (with the read_only
 * carve-out — computed targets are sealed at finalize, not user-answered),
 * attachment presence + multiplicity, number sign and min/max, text
 * max_length, and select option membership. A typed number below zero is
 * refused by "negative" alone, never also by its bounds; a computed value is
 * derived rather than typed, so it may run below zero and meets only the
 * bounds. Disabled and instruction items validate to
 * nothing (the guards live HERE, authoritatively — callers that also filter
 * do so for their own concerns, e.g. attachment-id collection), and so does a
 * display read-bound item: its answer is the sealed read the tester cannot
 * edit, never demanded and never checked against bounds, options or length
 * (the typed seal is always type-valid) — unless it carries a spine binding,
 * which the receipt demands whatever the read mode (validate.go isRequired).
 * At most one attachment finding per item. Present implies type-valid (the
 * exists operator already proved it), so the per-type casts only narrow what
 * the evaluator verified.
 */
export function validateAnswerItem(
  item: FormItem,
  values: Record<string, unknown>,
  enabled: ReadonlyMap<string, boolean>,
): AnswerValidationFinding[] {
  if (enabled.get(item.link_id) !== true) return [];
  if (item.type === "instruction") return [];
  const display = isDisplayReadBound(item);
  if (display && !isSpineBound(item)) return [];
  const findings: AnswerValidationFinding[] = [];
  const required = isRequiredAnswer(item) && item.read_only !== true;
  const raw = values[item.link_id];

  if (item.type === "document") {
    const ids = attachmentAnswerIds(raw);
    if (required && ids.length === 0) {
      findings.push({ link_id: item.link_id, rule: "attachment" });
    } else if (!multiAttachmentAllowed(item) && ids.length > 1) {
      findings.push({ link_id: item.link_id, rule: "attachment" });
    }
    return findings;
  }
  if (item.type === "signature") {
    if (required && signatureAnswerId(raw) === null) {
      findings.push({ link_id: item.link_id, rule: "attachment" });
    }
    return findings;
  }

  // A display item's presence is its sealed value, not its live read.
  const present = display ? storedAnswerPresent(item, raw) : answerPresent(item, values, enabled);
  if (!present) {
    if (required) findings.push({ link_id: item.link_id, rule: "required" });
    return findings;
  }
  if (display) return findings;
  if (item.type === "number") {
    const v = raw as number;
    if (item.computed === undefined && v < 0) {
      findings.push({ link_id: item.link_id, rule: "negative" });
      return findings;
    }
    if (item.validation?.min !== undefined && v < item.validation.min) {
      findings.push({ link_id: item.link_id, rule: "min" });
    }
    if (item.validation?.max !== undefined && v > item.validation.max) {
      findings.push({ link_id: item.link_id, rule: "max" });
    }
  } else if (item.type === "text") {
    const v = raw as string;
    if (item.validation?.max_length !== undefined && v.length > item.validation.max_length) {
      findings.push({ link_id: item.link_id, rule: "max_length" });
    }
  } else if (item.type === "select_one") {
    const v = raw as string;
    if (!(item.options ?? []).some((o) => o.value === v)) {
      findings.push({ link_id: item.link_id, rule: "option" });
    }
  } else if (item.type === "select_multiple") {
    const vs = raw as string[];
    const allowed = new Set((item.options ?? []).map((o) => o.value));
    if (!vs.every((v) => allowed.has(v))) {
      findings.push({ link_id: item.link_id, rule: "option" });
    }
  }
  return findings;
}

/**
 * The whole-document walk: top-level items in document order, then each
 * repeating collection of an enabled repeating section — shape and cap first
 * ("rows" on the section link_id; an absent answer is a blank collection,
 * never a finding), then every row's children under that row's own enabled
 * set. A disabled repeating section's collection is skipped whole: the seal
 * prunes it, so the gate never refuses its own seal. Operates on
 * whatever answers document it is given; a caller that wants computed
 * values bounds-checked like any other number seals first (see
 * ./computed-seal.ts), which is how the finalize pipeline orders the two.
 */
export function validateAnswers(
  definition: FormDefinition,
  answers: AnswerMap,
  reads?: ReadValues,
): AnswerValidationFinding[] {
  const enabled = enabledSet(definition, answers, reads);
  const findings: AnswerValidationFinding[] = [];
  for (const item of flattenItems(definition).ordered) {
    findings.push(...validateAnswerItem(item, answers, enabled));
  }
  for (const section of repeatingSections(definition)) {
    if (enabled.get(section.link_id) !== true) continue;
    const raw = answers[section.link_id];
    if (raw === undefined || raw === null) continue;
    if (!isRowArray(raw) || raw.length > MAX_COLLECTION_ROWS) {
      findings.push({ link_id: section.link_id, rule: "rows" });
      continue;
    }
    for (const row of raw) {
      const rowEnabled = rowEnabledSet(section, row);
      for (const item of section.items) {
        findings.push(...validateAnswerItem(item, row, rowEnabled));
      }
    }
  }
  return findings;
}
